import React, { useState, useEffect } from 'react';
import api from './api';

const BODY_PART_LABELS = {
  lowerback:  'Lower Back',
  middleback: 'Middle Back',
  upperback:  'Upper Back',
  cervical:   'Cervical / Neck',
  hip:        'Hip',
  knee:       'Knee',
  shoulder:   'Shoulder',
  ankle:      'Ankle',
};

const YT_BASE = 'https://www.youtube.com/watch?v=';

export default function VideoAssignModal({ patientCard, bodyPart, onClose }) {
  const [videos,   setVideos]   = useState([]);
  const [loading,  setLoading]  = useState(true);
  const [toggling, setToggling] = useState(null); // video._id being toggled
  const [assignLabel, setAssignLabel] = useState('recommended');

  useEffect(() => {
    const fetchVideos = async () => {
      setLoading(true);
      try {
        const res = await api.get(`/resource-videos?bodyPart=${bodyPart}`);
        setVideos(res.data || []);
      } catch {
        setVideos([]);
      } finally {
        setLoading(false);
      }
    };
    fetchVideos();
  }, [bodyPart]);

  const handleToggle = async (video) => {
    const isAssigned = (video.assignedPatients || []).includes(patientCard);
    setToggling(video._id);
    try {
      const endpoint = isAssigned ? 'unassign' : 'assign';
      const res = await api.put(`/resource-videos/${video._id}/${endpoint}`, { patientCard, label: assignLabel });
      setVideos(prev => prev.map(v => v._id === video._id ? res.data : v));
    } catch { /* silent */ }
    setToggling(null);
  };

  const bodyPartLabel = BODY_PART_LABELS[bodyPart] || bodyPart;

  return (
    <div style={{
      position: 'fixed', inset: 0, background: 'rgba(0,0,0,.5)',
      display: 'flex', alignItems: 'center', justifyContent: 'center',
      zIndex: 9500, padding: 16,
    }}>
      <div style={{
        background: '#fff', borderRadius: 14, width: '100%', maxWidth: 560,
        maxHeight: '85vh', display: 'flex', flexDirection: 'column',
        boxShadow: '0 12px 40px rgba(0,0,0,.22)',
      }}>
        {/* Header */}
        <div style={{
          display: 'flex', alignItems: 'center', justifyContent: 'space-between',
          padding: '16px 20px', borderBottom: '1px solid #f3f4f6',
        }}>
          <div>
            <div style={{ fontSize: 16, fontWeight: 700, color: '#1e1b4b' }}>
              📹 Related Videos
            </div>
            <div style={{ fontSize: 12, color: '#6b7280', marginTop: 2 }}>
              Body part: <strong>{bodyPartLabel}</strong>
            </div>
          </div>
          <button
            onClick={onClose}
            style={{
              background: 'none', border: 'none', fontSize: 20,
              cursor: 'pointer', color: '#6b7280', lineHeight: 1,
            }}
          >
            ×
          </button>
        </div>

        {/* Label picker */}
        <div style={{ display: 'flex', gap: 6, padding: '12px 20px 0' }}>
          {[{ k: 'mandatory', icon: '\uD83D\uDD34', c: '#dc2626', bg: '#fef2f2', bc: '#fca5a5' },
            { k: 'recommended', icon: '\uD83D\uDFE1', c: '#d97706', bg: '#fffbeb', bc: '#fcd34d' },
            { k: 'optional', icon: '\uD83D\uDFE2', c: '#16a34a', bg: '#f0fdf4', bc: '#86efac' }].map(o => (
            <button key={o.k} onClick={() => setAssignLabel(o.k)} style={{
              flex: 1, padding: '7px 4px', borderRadius: 8, fontSize: 11, fontWeight: 700, cursor: 'pointer',
              background: assignLabel === o.k ? o.bg : '#f9fafb', color: assignLabel === o.k ? o.c : '#9ca3af',
              border: `2px solid ${assignLabel === o.k ? o.bc : '#e5e7eb'}`, transition: 'all 0.15s',
            }}>{o.icon} {o.k[0].toUpperCase() + o.k.slice(1)}</button>
          ))}
        </div>

        {/* Content */}
        <div style={{ overflowY: 'auto', flex: 1, padding: '16px 20px' }}>
          {loading ? (
            <div style={{ color: '#9ca3af', fontSize: 14, textAlign: 'center', padding: '24px 0' }}>
              Loading videos…
            </div>
          ) : videos.length === 0 ? (
            <div style={{ color: '#9ca3af', fontSize: 13, textAlign: 'center', padding: '24px 0', lineHeight: 1.6 }}>
              No videos saved for <strong>{bodyPartLabel}</strong> yet.<br />
              Go to <strong>Resources</strong> to save and tag videos.
            </div>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
              {videos.map(v => {
                const isAssigned = (v.assignedPatients || []).includes(patientCard);
                const busy = toggling === v._id;
                return (
                  <div key={v._id} style={{
                    display: 'flex', gap: 12, alignItems: 'center',
                    border: isAssigned ? '1.5px solid #a5b4fc' : '1.5px solid #e5e7eb',
                    borderRadius: 10, padding: '10px 12px',
                    background: isAssigned ? '#eef2ff' : '#fff',
                  }}>
                    <a href={`${YT_BASE}${v.youtubeId}`} target="_blank" rel="noopener noreferrer" style={{ flexShrink: 0 }}>
                      <img src={v.thumbnail} alt={v.title}
                        style={{ width: 88, height: 50, objectFit: 'cover', borderRadius: 6, display: 'block' }} />
                    </a>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{
                        fontSize: 13, fontWeight: 600, color: '#111827',
                        lineHeight: 1.35, marginBottom: 3,
                        overflow: 'hidden', textOverflow: 'ellipsis',
                        display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical',
                      }}>
                        {v.title}
                      </div>
                      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4 }}>
                        {(v.bodyParts || []).map(tag => (
                          <span key={tag} style={{
                            background: '#ede9fe', color: '#4f46e5', borderRadius: 10,
                            padding: '2px 7px', fontSize: 10, fontWeight: 600,
                          }}>
                            {BODY_PART_LABELS[tag] || tag}
                          </span>
                        ))}
                      </div>
                    </div>
                    <button
                      onClick={() => handleToggle(v)}
                      disabled={busy}
                      style={{
                        flexShrink: 0, padding: '7px 14px', borderRadius: 8,
                        border: isAssigned ? '1.5px solid #a5b4fc' : '1.5px solid #4f46e5',
                        background: isAssigned ? '#fff' : '#4f46e5',
                        color: isAssigned ? '#4f46e5' : '#fff',
                        fontSize: 12, fontWeight: 700,
                        cursor: busy ? 'default' : 'pointer',
                        whiteSpace: 'nowrap',
                      }}
                    >
                      {busy ? '…' : isAssigned ? '✓ Assigned' : 'Assign'}
                    </button>
                  </div>
                );
              })}
            </div>
          )}
        </div>

        {/* Footer */}
        <div style={{
          padding: '12px 20px', borderTop: '1px solid #f3f4f6',
          display: 'flex', justifyContent: 'flex-end',
        }}>
          <button
            onClick={onClose}
            style={{
              padding: '8px 22px', borderRadius: 8,
              border: '1.5px solid #d1d5db', background: '#f9fafb',
              color: '#374151', fontSize: 13, fontWeight: 600, cursor: 'pointer',
            }}
          >
            Close
          </button>
        </div>
      </div>
    </div>
  );
}
